/*
 * The invoice's rules (SQ-598), after Android's CompleteJob.kt: where the
 * payments stand against the Job Total, what a payment amount may be and why
 * not, the notice when an edit or a payment moves an invoice between Paid and
 * not, and who may edit it. Kept apart from the page so the tests can read it.
 */

const CENT = 0.005;

/** SQ-401: an invoice is edited (through its job) in these statuses; never once Deleted. */
const EDITABLE_INVOICE_STATUSES = ['Created', 'Sent', 'Overdue', 'Paid'];

const isStatus = (status: string | null | undefined, name: string) => (status ?? '').toLowerCase() === name.toLowerCase();

/** isInvoiceEditable: Edit on an invoice that is not Deleted, for someone with invoices.edit. */
export const isInvoiceEditable = (status: string | null | undefined, canEditInvoices: boolean) =>
  canEditInvoices && EDITABLE_INVOICE_STATUSES.some(s => isStatus(status, s));

/** The invoice's status as the header shows it; Deleted is ignored. */
export const invoiceStatusLabel = (status: string | null | undefined) =>
  status && !isStatus(status, 'Deleted') ? status : '';

/** Invoice total minus Amount Paid, floored at zero. */
export const invoiceRemainingBalance = (total: number, paid: number) => Math.max(0, total - paid);

/** InvoicePaymentStanding: what is left, what was paid beyond the total, and whether it is paid in full. */
export interface PaymentStanding {
  paid: number;
  remaining: number;
  overpaid: number;
  paidInFull: boolean;
}

/**
 * The numbers decide, so an invoice being edited reads right before the
 * server's new status arrives (SQ-401). A Paid invoice with no payment on
 * file was paid before payments were recorded: in full.
 */
export const paymentStanding = (total: number, paid: number, status: string | null | undefined): PaymentStanding => {
  const remaining = invoiceRemainingBalance(total, paid);
  const over = Math.max(0, paid - total);
  const isPaid = isStatus(status, 'Paid');
  return {
    paid,
    remaining,
    overpaid: over >= CENT ? over : 0,
    paidInFull: (isPaid && paid < CENT) || ((isPaid || total > 0) && remaining < CENT),
  };
};

/** One line of the customer's totals: its label and its dollars. */
export interface CustomerTotalLine {
  label: string;
  amount: number;
}

/**
 * What the customer's copy of an invoice or receipt shows under the work
 * (SQ-337): Total, Amount Paid, and Balance Due, or Overpaid when more came
 * in. A Paid invoice with no payment on file was paid before payments were
 * recorded: in full. [payable]: a balance is due, so Pay is offered.
 */
export const customerInvoiceTotals = (total: number, paid: number, status: string | null | undefined): {lines: CustomerTotalLine[]; payable: boolean} => {
  const standing = paymentStanding(total, paid, status);
  const paidShown = isStatus(status, 'Paid') && paid < CENT ? total : paid;
  const balance = standing.paidInFull ? 0 : standing.remaining;
  return {
    lines: [
      {label: 'Total', amount: total},
      {label: 'Amount Paid', amount: paidShown},
      standing.overpaid > 0 ? {label: 'Overpaid', amount: standing.overpaid} : {label: 'Balance Due', amount: balance},
    ],
    payable: balance >= CENT,
  };
};

type PaymentField ={amount: number} | {invalid: string};

const parsePaymentField = (raw: string): PaymentField => {
  const cleaned = raw.replace(/,/g, '').replace(/\$/g, '').trim();
  if (!cleaned) return {invalid: 'Enter the amount the customer paid.'};
  const value = Number(cleaned);
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(cleaned) || !Number.isFinite(value)) return {invalid: 'Enter a valid amount.'};
  if (value <= 0) return {invalid: 'Enter the amount the customer paid.'};
  return {amount: value};
};

/** parseAmountPaid: digits and an optional decimal, $ and commas ignored. Blank, non-numeric and negative are not an amount. */
export const parseAmountPaid = (raw: string): number | null => {
  const cleaned = raw.replace(/,/g, '').replace(/\$/g, '').trim();
  if (!cleaned || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

/** Why a Partial Payment cannot be recorded, or null when it is more than zero and no more than what is owed. */
export const paymentAmountRejection = (raw: string, remaining: number): string | null => {
  const parsed = parsePaymentField(raw);
  if ('invalid' in parsed) return parsed.invalid;
  if (parsed.amount > remaining + CENT) return 'That is more than what is owed. Choose Paid in Full to record an overpayment.';
  return null;
};

/** Why a Paid in Full amount cannot be recorded, or null when it covers what is owed (more is an overpayment, and fine). */
export const fullPaymentRejection = (raw: string, remaining: number): string | null => {
  const parsed = parsePaymentField(raw);
  if ('invalid' in parsed) return parsed.invalid;
  if (parsed.amount < remaining - CENT) return 'That is less than what is owed. Choose Partial Payment for part of it.';
  return null;
};

export const formatAmountPaidInput = (amount: number) => amount.toFixed(2);

/**
 * SQ-401: the pop-up when an edit (or a payment on a Created invoice) moves an
 * invoice between Paid and not. Null for any other move. Never says an
 * invoice is being sent: sending is always the person's own action.
 */
export const invoiceStatusMoveNotice = (
  from: string | null | undefined, to: string | null | undefined, total: number, paid: number, formatMoney: (n: number) => string,
): string | null => {
  if (isStatus(from, 'Paid') && (isStatus(to, 'Sent') || isStatus(to, 'Created'))) {
    return `Moved back to ${isStatus(to, 'Sent') ? 'Sent' : 'Created'}. The invoice total is now `
      + `${formatMoney(Math.max(0, total - paid))} more than what's been paid, so this invoice is no longer paid in full.`;
  }
  if (!isStatus(from, 'Paid') && isStatus(to, 'Paid') && (isStatus(from, 'Created') || isStatus(from, 'Sent') || isStatus(from, 'Overdue'))) {
    return "Moved to Paid. What's been paid now covers the full invoice total.";
  }
  return null;
};

/** A server time as an instant. The API writes UTC, often with no zone mark; null when unset or unreadable. */
const parseServerTime = (value: string | null | undefined): Date | null => {
  const text = value?.trim();
  if (!text) return null;
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** A payment's date as M/d/yy, in local time. */
export const formatPaymentDate = (createdAt: string) => {
  const date = parseServerTime(createdAt);
  return date ? `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}` : createdAt.trim();
};

// ---- SQ-687 / SQ-688: the due date and the follow-ups after it ----------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A calendar day at local midnight. */
const dayOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** The invoice's due instant (UTC, with or without its zone mark) as a day on this device; null when unset or unreadable. */
export const invoiceDueDate = (dueAt: string | null | undefined): Date | null => {
  const date = parseServerTime(dueAt);
  return date ? dayOf(date) : null;
};

/** "Nov 4, 2026". */
export const formatDueDate = (day: Date) => `${MONTHS[day.getMonth()]} ${day.getDate()}, ${day.getFullYear()}`;

/** "Nov 7". */
export const formatFollowUpDate = (day: Date) => `${MONTHS[day.getMonth()]} ${day.getDate()}`;

/** Past due once today is after the due date. */
export const isPastDue = (due: Date, now: Date = new Date()) => dayOf(now).getTime() > due.getTime();

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The follow-ups still to come, as days on this device. The server books
 * each at the due instant plus its days (whole 24-hour days, UTC), so that
 * instant is what is shown and what is compared with now: one that went out
 * earlier today is gone, and the day is the one it is sent on here.
 */
export const upcomingFollowUps = (
  dueAt: string | null | undefined, followUpDays: number[] | null | undefined, now: Date = new Date(),
): Date[] => {
  const due = parseServerTime(dueAt);
  if (!due) return [];
  return (followUpDays ?? [])
    .map(days => new Date(due.getTime() + days * DAY_MS))
    .filter(at => at.getTime() > now.getTime())
    .map(dayOf);
};
